import { Tooltip, TooltipContent, TooltipTrigger } from '@design-system';
import { DAYS_IN_MONTH } from '@shared/calculations/funding/projection/sharedConstants';
import { BalanceBreakdownTooltip } from '../BalanceBreakdownTooltip';
import { InflationTooltipContent } from '../cells';
import { currency } from '../formatters';
import { PROJECTED_COST_NOTE } from '../columnGroups';
import { GrowthFactor, HeaderTooltip } from '../HeaderTooltip';
import { AccumulationWhenCell, GrowthValueCell } from '../../accumulationTimeline/cells';
import type { AccumulationColumnSpec, AccumulationCtx } from '../../accumulationTimeline/types';
import type { StrategyPresentation } from './types';

function riderIncrementCount(policyYear: number, capYears: number): number {
  const rawIncrements = Math.max(Math.floor(policyYear) - 1, 0);
  const cap = capYears > 0 ? capYears : null;
  return cap !== null ? Math.min(rawIncrements, cap) : rawIncrements;
}

function CmTwoMonthlyBenefitTooltip({
  row,
  terms,
  baseMonthly,
}: {
  row: AccumulationYear;
  terms: ClaimTerms;
  baseMonthly: number;
}) {
  const riderYears = riderIncrementCount(row.policyYear, terms.inflationCapYears);
  const riderMonthly = Math.max(row.snapshot.monthlyBenefitCap - baseMonthly, 0);
  const ratePct = (terms.inflationRate * 100).toFixed(2);
  const formula = terms.inflationIsSimple
    ? 'base monthly x rider rate x rider years'
    : 'base monthly x ((1 + rider rate)^rider years - 1)';
  const capNote =
    terms.inflationCapYears > 0
      ? `Rider years are capped at ${terms.inflationCapYears} annual increases.`
      : 'Rider years are policy year - 1.';

  return (
    <TooltipContent className="text-xs">
      <div className="mb-1 font-semibold">Policy year {row.policyYear} monthly benefit</div>
      <div className="mb-1 text-[10px] text-gray-300">
        Before care begins, this is the base monthly benefit plus this policy year&apos;s inflation
        rider.
      </div>
      <div className="grid grid-cols-[auto_auto] gap-x-3 gap-y-0.5">
        <span className="text-gray-300">Base monthly</span>
        <span className="font-semibold tabular-nums">{currency(baseMonthly)}</span>
        <span className="text-gray-300">Rider formula</span>
        <span className="font-semibold tabular-nums">{formula}</span>
        <span className="text-gray-300">Rider rate</span>
        <span className="font-semibold tabular-nums">{ratePct}%</span>
        <span className="text-gray-300">Rider years</span>
        <span className="font-semibold tabular-nums">{riderYears}</span>
        <span className="text-gray-300">Rider amount</span>
        <span className="font-semibold tabular-nums">{currency(riderMonthly)}</span>
        <span className="text-gray-300">Monthly benefit</span>
        <span className="font-semibold tabular-nums">
          {currency(baseMonthly)} + {currency(riderMonthly)} ={' '}
          {currency(row.snapshot.monthlyBenefitCap)}
        </span>
      </div>
      <div className="mt-2 text-[10px] text-gray-300">{capNote}</div>
    </TooltipContent>
  );
}

function CmTwoDeathBenefitTooltip({ row }: { row: AccumulationYear }) {
  return (
    <TooltipContent className="text-xs">
      <div className="mb-1 font-semibold">Policy year {row.policyYear} death benefit</div>
      <div className="mb-1 text-[10px] text-gray-300">
        Before care begins, this is the policy&apos;s flat specified amount. The inflation rider
        grows the monthly LTC benefit, not the death benefit.
      </div>
      <div className="grid grid-cols-[auto_auto] gap-x-3 gap-y-0.5">
        <span className="text-gray-300">Policy amount</span>
        <span className="font-semibold tabular-nums">
          {currency(row.snapshot.effectiveFaceAmount)}
        </span>
        <span className="text-gray-300">Death benefit shown</span>
        <span className="font-semibold tabular-nums">
          {currency(row.snapshot.effectiveFaceAmount)}
        </span>
      </div>
    </TooltipContent>
  );
}

// CareMatters II accumulation: the base monthly benefit pool is flat — growth
// lives in the escalating rider, which lifts the monthly maximum each policy
// year. So we surface the Monthly benefit (rider escalation) and the Death
// benefit, and drop the flat LTC pool the universal default would show with a
// misleading "grows on X%" tooltip.
function buildCmTwoAccumulationColumns({
  terms,
  timeline,
}: AccumulationCtx): AccumulationColumnSpec[] {
  const columns: AccumulationColumnSpec[] = [
    {
      key: 'when',
      label: 'Policy year',
      sticky: true,
      render: row => <AccumulationWhenCell row={row} />,
    },
  ];

  if (timeline.some(r => r.snapshot.monthlyBenefitCap > 0.5)) {
    const baseMonthly = terms.nominalDailyBenefitBase * DAYS_IN_MONTH;
    columns.push({
      key: 'monthly-benefit',
      label: 'Monthly benefit',
      tooltip: (
        <HeaderTooltip
          description="Before care begins, this shows the CareMatters II monthly LTC benefit for each policy year: the base monthly benefit plus that year's inflation rider."
          formulas={[
            { label: 'Base monthly', expression: 'daily benefit at issue x 30' },
            {
              label: 'Rider',
              expression: 'base monthly x ((1 + rider rate)^rider years - 1)',
            },
            { label: 'Shown amount', expression: 'base monthly + rider' },
          ]}
          notes="Rider years are policy year - 1, capped if the rider has a limit. Simple-interest riders use base monthly x rider rate x rider years."
        />
      ),
      render: (row, prev) => (
        <GrowthValueCell
          value={row.snapshot.monthlyBenefitCap}
          prevValue={prev?.snapshot.monthlyBenefitCap ?? null}
          tooltip={
            <CmTwoMonthlyBenefitTooltip
              row={row}
              terms={terms}
              baseMonthly={baseMonthly}
            />
          }
        />
      ),
    });
  }

  if (timeline.some(r => r.snapshot.effectiveFaceAmount > 0.5)) {
    columns.push({
      key: 'death-benefit',
      label: 'Death benefit',
      tooltip: (
        <HeaderTooltip
          description="Before care begins, this shows the policy death benefit amount. CareMatters II's rider grows the monthly LTC benefit, not the death benefit."
          formulas={[
            { label: 'Input', expression: 'policy specified amount' },
            { label: 'Shown amount', expression: 'policy specified amount' },
          ]}
          notes="Claim-month drawdowns are not included in this before-care timeline."
        />
      ),
      render: (row, prev) => (
        <GrowthValueCell
          value={row.snapshot.effectiveFaceAmount}
          prevValue={prev?.snapshot.effectiveFaceAmount ?? null}
          tooltip={<CmTwoDeathBenefitTooltip row={row} />}
        />
      ),
    });
  }

  return columns;
}

// CM II's pool / death-benefit / payout tooltips read the strategy's per-month
// deltas (base + rider drawdown, EP lump, indemnity surplus accrual/application).
// The Surplus balance column itself is now shared across all indemnity products
// (see `buildPayoutColumnGroups`); this type is only for the cell tooltips below.
type CmTwoLikeDeltas = {
  baseBenefitPaid?: number;
  riderBenefitPaid?: number;
  epLumpSumPaid?: number;
  epLumpSumBasePaid?: number;
  epLumpSumDays?: number;
  epLumpSumDailyRate?: number;
  indemnitySurplusAccrued?: number;
  indemnitySurplusApplied?: number;
};

function EpLumpTooltipContent({
  days,
  dailyRate,
  total,
}: {
  days: number;
  dailyRate: number;
  total: number;
}) {
  return (
    <TooltipContent className="text-xs">
      <div className="mb-1 font-semibold">Waiting period reimbursement</div>
      <div className="mb-1 text-[10px] text-gray-300">
        Once the waiting period is satisfied, days served during it are paid back as a lump sum at
        the inflated daily benefit on the satisfaction date.
      </div>
      <div className="grid grid-cols-[auto_auto] gap-x-3 gap-y-0.5">
        <span className="text-gray-300">Days served</span>
        <span className="font-semibold tabular-nums">{days}</span>
        <span className="text-gray-300">Inflated daily rate</span>
        <span className="font-semibold tabular-nums">{currency(dailyRate)}/day</span>
        <span className="text-gray-300">Lump sum</span>
        <span className="font-semibold tabular-nums">
          {days} × {currency(dailyRate)} = {currency(total)}
        </span>
      </div>
    </TooltipContent>
  );
}

export const cmTwoPresentation: StrategyPresentation = {
  accumulationColumns: buildCmTwoAccumulationColumns,

  benefitAmountCell: ({ month, terms }) => {
    // Pre-policy rows carry no applied cap; fall through to the default cell
    // (which renders a dash when there's no snapshot for the year).
    if (month.events.ineligibilityReason === 'pre-policy') return null;
    const baseDaily =
      terms.nominalDailyBenefitByEnvironment?.[month.context.careEnvironment] ??
      terms.nominalDailyBenefitBase;
    const baseMonthly = baseDaily * DAYS_IN_MONTH;
    const isSimple = terms.inflationIsSimple === true;
    // Headline = the engine's own applied base + rider ceiling for this row.
    // The rider steps on the same anniversary clock as the row (P2-1), so the
    // formula below reconciles exactly with the headline (P2-8). Base + rider
    // is algebraically baseMonthly × growth factor, so it maps onto the
    // shared inflation tooltip (compound: ×(1+r)^n; simple: ×(1 + r·n)).
    const monthlyCap = month.facts.appliedMonthlyBenefitCap;

    const intYear = Math.floor(month.context.policyYear);
    const cap = terms.inflationCapYears > 0 ? terms.inflationCapYears : null;
    // Increment count = policyYear − 1, capped at capYears — the same formula
    // as the engine's calcRiderMonthly at the row's policy year.
    const rawIncrements = Math.max(intYear - 1, 0);
    const increments = cap !== null ? Math.min(rawIncrements, cap) : rawIncrements;
    const capped = cap !== null && intYear > cap;

    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="cursor-help leading-tight">
            <div className="font-medium text-gray-900">{currency(monthlyCap)}</div>
            <div className="text-[10px] text-gray-500">
              {currency(monthlyCap / DAYS_IN_MONTH)}/day
            </div>
          </div>
        </TooltipTrigger>
        <InflationTooltipContent
          header={`Policy year ${intYear}`}
          startingLabel="At issue"
          startingValue={baseMonthly}
          rate={terms.inflationRate}
          years={increments}
          result={monthlyCap}
          note={capped ? `Rider capped at year ${cap}` : undefined}
          compoundLabel={isSimple ? 'Simple growth' : 'Compound rider growth'}
          isSimple={isSimple}
        />
      </Tooltip>
    );
  },

  benefitAmountValueForMonth: ({ month }) =>
    month.events.ineligibilityReason === 'pre-policy'
      ? undefined
      : month.facts.appliedMonthlyBenefitCap,

  poolCellTooltip: ({ month, openState }) => {
    const open = openState.pools.baseBenefitPool;
    const close = month.state.pools.baseBenefitPool;
    const sd = month.deltas.strategyDeltas as CmTwoLikeDeltas;
    const baseDrawn = sd.baseBenefitPaid ?? 0;
    const lumpBase = sd.epLumpSumBasePaid ?? 0;
    if (open === 0 && close === 0 && baseDrawn === 0 && lumpBase === 0) return null;
    return (
      <BalanceBreakdownTooltip
        title="LTC pool"
        description="CareMatters II's base pool. Only the base monthly benefit (and the base portion of any EP retroactive lump) draws down the pool — the inflation rider is paid on top and does not deplete it."
        sections={[
          {
            opening: open,
            closing: close,
            lines: [
              { label: 'Base benefit drawn', value: baseDrawn, kind: 'subtract' },
              { label: 'EP retroactive (base portion)', value: lumpBase, kind: 'subtract' },
            ],
          },
        ]}
      />
    );
  },

  deathBenefitCellTooltip: ({ month, openState }) => {
    const open = openState.pools.deathBenefit;
    const close = month.state.pools.deathBenefit;
    const sd = month.deltas.strategyDeltas as CmTwoLikeDeltas;
    const baseDrawn = sd.baseBenefitPaid ?? 0;
    const lumpBase = sd.epLumpSumBasePaid ?? 0;
    if (open === 0 && close === 0 && baseDrawn === 0 && lumpBase === 0) return null;
    // The actual reduction is the type-contract field (open − close, capped
    // at the minimum-guaranteed floor) — it can be LESS than the raw base +
    // lump drawdown when the floor binds (P2-6). Itemize the raw drawdown
    // and add the floor adjustment back so the lines reconcile to closing.
    const reduction = month.deltas.deathBenefit.reduction;
    const rawDrawdown = baseDrawn + lumpBase;
    const floorAdjustment = Math.max(rawDrawdown - reduction, 0);
    return (
      <BalanceBreakdownTooltip
        title="Death benefit"
        description="Flat Face Amount, depleted by the base benefit payout (and the base portion of any EP retroactive lump). The inflation rider is paid on top and does not deplete the death benefit. Floors at the minimum guaranteed amount."
        sections={[
          {
            opening: open,
            closing: close,
            lines: [
              { label: 'Base benefit drawn', value: baseDrawn, kind: 'subtract' },
              { label: 'EP retroactive (base portion)', value: lumpBase, kind: 'subtract' },
              ...(floorAdjustment > 0.005
                ? [
                    {
                      label: 'Floored at minimum guaranteed',
                      value: floorAdjustment,
                      kind: 'add' as const,
                    },
                  ]
                : []),
            ],
          },
        ]}
      />
    );
  },

  payoutCellTooltip: ({ month }) => {
    const sd = month.deltas.strategyDeltas as CmTwoLikeDeltas;
    const basePay = sd.baseBenefitPaid ?? 0;
    const riderPay = sd.riderBenefitPaid ?? 0;
    const total = basePay + riderPay;
    const cost = month.facts.effectiveCareCost;
    const indemnityPayout = month.deltas.payout.indemnityPayout;
    const surplusApplied = sd.indemnitySurplusApplied ?? 0;
    const surplusAccrued = sd.indemnitySurplusAccrued ?? 0;
    if (total === 0 && (sd.epLumpSumPaid ?? 0) === 0) return null;

    // The surplus math runs against the ELIGIBLE-day slice of the month's
    // cost, not the full month (P2-5) — label both and narrate against the
    // figure the engine actually compared.
    const eligibleDays = month.facts.coverageEligibleDays;
    const isPartialMonth = eligibleDays < DAYS_IN_MONTH;
    const eligibleCost = month.facts.dailyCareCost * eligibleDays;
    const rule =
      indemnityPayout >= eligibleCost
        ? "CareMatters II pays a fixed monthly indemnity benefit regardless of actual cost. This month's benefit covers the eligible cost; the excess accrues as a surplus reserve."
        : "CareMatters II pays a fixed monthly indemnity benefit. The benefit is below this month's eligible cost — the surplus reserve (if any) covers part of the gap; the remainder is out-of-pocket.";

    return (
      <TooltipContent className="max-w-xs text-xs">
        <div className="mb-1 font-semibold">Projected payout — indemnity</div>
        <div className="mb-1 text-[10px] text-gray-300">{rule}</div>
        <div className="grid grid-cols-[auto_auto] gap-x-3 gap-y-0.5">
          <span className="text-gray-300">Projected monthly care cost</span>
          <span className="font-semibold tabular-nums">{currency(cost)}</span>
          {isPartialMonth && (
            <>
              <span className="text-gray-300">Eligible portion ({eligibleDays} days)</span>
              <span className="font-semibold tabular-nums">{currency(eligibleCost)}</span>
            </>
          )}
          <span className="text-gray-300">Base benefit</span>
          <span className="font-semibold tabular-nums">{currency(basePay)}</span>
          {riderPay > 0 && (
            <>
              <span className="text-gray-300">+ Inflation rider</span>
              <span className="font-semibold tabular-nums text-emerald-300">
                +{currency(riderPay)}
              </span>
            </>
          )}
          <span className="text-gray-300">Total paid</span>
          <span className="font-semibold tabular-nums">{currency(total)}</span>
          {surplusAccrued > 0 && (
            <>
              <span className="text-gray-300">Surplus accrued</span>
              <span className="font-semibold tabular-nums text-emerald-300">
                +{currency(surplusAccrued)}
              </span>
            </>
          )}
          {surplusApplied > 0 && (
            <>
              <span className="text-gray-300">Surplus applied</span>
              <span className="font-semibold tabular-nums text-emerald-300">
                +{currency(surplusApplied)}
              </span>
            </>
          )}
        </div>
      </TooltipContent>
    );
  },

  paidCellExtras: month => {
    const sd = month.deltas.strategyDeltas as CmTwoLikeDeltas;
    const surplusApplied = sd.indemnitySurplusApplied ?? 0;
    const epLump = sd.epLumpSumPaid ?? 0;
    const covered = month.deltas.payout.ltcCoveragePaid;
    const withSurplus = month.deltas.payout.ltcPayoutToCost;

    if (surplusApplied === 0 && epLump === 0) return null;

    return (
      <>
        {surplusApplied > 0 && withSurplus !== covered && (
          <div className="inline-block text-[10px] text-emerald-700">
            +{currency(surplusApplied)} surplus
          </div>
        )}
        {epLump > 0 && (
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="inline-block cursor-help border-b border-dotted border-amber-300 text-[10px] text-amber-700">
                +{currency(epLump)} WP reimbursement
              </div>
            </TooltipTrigger>
            <EpLumpTooltipContent
              days={sd.epLumpSumDays ?? 0}
              dailyRate={sd.epLumpSumDailyRate ?? 0}
              total={epLump}
            />
          </Tooltip>
        )}
      </>
    );
  },

  // CM II's base pool is flat — it does not inflate at policy anniversaries.
  // The rider grows the monthly benefit instead. A "Pool limit" column would
  // just echo the static base pool every year, so we hide it.
  hiddenColumns: ['pool-limit'],

  // CM II is a single-pool product (base pool + rider-on-top) — no
  // Acceleration/Continuation split, so the generic AOB/COB framing in the
  // Drawn cell would imply a structure the product doesn't have (P2-7).
  drawnCellLabels: {
    description:
      'Cumulative reduction of the LTC benefit pool. Only the base benefit ' +
      '(and the base portion of any EP retroactive lump) draws the pool down — ' +
      'the inflation rider is paid on top and does not deplete it.',
    baseLabel: 'Benefit pool drawn',
    singlePool: true,
  },

  copy: {
    poolTooltip: (
      <HeaderTooltip
        description="End-of-month LTC base-pool balance, with this month's change beneath. The base pool is flat (no anniversary inflation) — the inflation rider grows the monthly benefit instead."
        formulas={[
          { label: 'Opening pool', expression: 'base monthly benefit × benefit period (months)' },
          { label: 'Closing pool', expression: 'opening − base benefit drawn this month' },
          { label: 'Rider', expression: 'paid on top of base; does not deplete the pool' },
        ]}
        notes="The base portion of any waiting-period retroactive lump also draws the pool."
      />
    ),
    payoutColumnTooltip: (
      <HeaderTooltip
        description="Indemnity payout this month, with cumulative dollars paid by the policy through this month in parentheses. CareMatters II pays a fixed monthly indemnity benefit regardless of actual cost."
        formulas={[
          {
            label: 'Monthly payout',
            expression: 'base benefit + inflation rider (+ retroactive WP lump, if any)',
          },
          { label: 'Cumulative', expression: 'Σ monthly payouts through this month' },
        ]}
        notes="When the benefit exceeds cost, the surplus accrues to the indemnity reserve; when cost exceeds the benefit, the reserve is drawn down to close the gap."
      />
    ),
    costTooltip: (
      <HeaderTooltip
        description="Projected total monthly care cost (with inflated per-day rate beneath). The daily rate comes from the care plan's phase × environment unit cost."
        formulas={[
          {
            label: 'Daily cost',
            expression: (
              <>
                today's daily unit cost ×{' '}
                <GrowthFactor
                  rate="care-cost inflation"
                  exponent="years from today"
                />
              </>
            ),
          },
          { label: 'Monthly cost', expression: 'daily cost × days in month' },
        ]}
        notes={PROJECTED_COST_NOTE}
      />
    ),
    benefitAmountTooltip: (
      <HeaderTooltip
        description="Policy monthly benefit maximum for this policy year (base + inflation rider), with the daily equivalent beneath."
        formulas={[
          { label: 'Base monthly', expression: 'at-issue daily benefit × days in month' },
          {
            label: 'With rider',
            expression: (
              <>
                base monthly ×{' '}
                <GrowthFactor
                  rate="rider rate"
                  exponent="policy year − 1"
                />
              </>
            ),
          },
        ]}
        notes="Rider growth is compound by default and capped at the rider's cap year if specified; simple-interest variants use (1 + rate × years) instead."
      />
    ),
    drawnTooltip: (
      <HeaderTooltip
        description="Cumulative reduction of the base pool through this month. The inflation rider is paid on top and does not draw down the pool."
        formulas={[
          { label: 'Drawn', expression: 'Σ (base benefit drawn each month)' },
          { label: 'Identity', expression: 'Base pool − Drawn = Base pool remaining' },
        ]}
        notes="The base portion of any WP retroactive lump is included in this total."
      />
    ),
    deathBenefitTooltip: (
      <HeaderTooltip
        description="End-of-month death benefit, with this month's change beneath. CareMatters II has a flat Face Amount that is depleted dollar-for-dollar by the base benefit (not the rider)."
        formulas={[
          { label: 'Death benefit', expression: 'at-issue Face Amount − Σ (base benefit drawn)' },
          { label: 'Floor', expression: 'max(above, minimum guaranteed amount)' },
        ]}
      />
    ),
    benefitInflationCompoundLabel: 'Compound rider growth',
    benefitInflationCapNote: capYear => `Rider capped at year ${capYear}`,
  },
};
